
import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { Customer } from './types';
import { getCustomersWithStatusFilter } from '@/lib/supabase/customers/queries/statusCustomers';

export const useCustomers = (shopId?: string) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    console.log('[useCustomers] Fetching customers');
    
    try {
      // Get organization ID from localStorage
      const organizationId = localStorage.getItem('currentOrganizationId');
      
      if (!organizationId) {
        console.error("[useCustomers] No organization ID found in localStorage");
        toast({
          title: "Error",
          description: "Organization ID not found",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }
      
      console.log(`[useCustomers] Using organization ID: ${organizationId} for shop: ${shopId || 'all'}`);
      
      // Log current timestamp to track query execution time
      const startTime = new Date().getTime();
      
      // Fetch customers with all status (use 'all' as status parameter)
      const fetchedCustomers = await getCustomersWithStatusFilter(organizationId, 'all', shopId);
      
      const endTime = new Date().getTime();
      console.log(`[useCustomers] Query completed in ${endTime - startTime}ms`);
      console.log(`[useCustomers] Retrieved ${fetchedCustomers.length} customers for organization ${organizationId}`);
      
      // Set the customers directly 
      setCustomers(fetchedCustomers);
    } catch (error) {
      console.error('[useCustomers] Error:', error);
      toast({
        title: "Error",
        description: "Failed to load customers",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast, shopId]);
  
  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Add a new customer to the list
  const addCustomer = useCallback((customer: Customer) => {
    console.log('[useCustomers] Adding new customer to list:', customer);
    setCustomers(prev => [customer, ...prev]);
  }, []);

  return {
    customers,
    loading,
    addCustomer,
    refreshCustomers: fetchCustomers
  };
};
